import { LineChartIcon, BarChartIcon } from "@flat-ui";

/** Supported chart visualization modes for the analytics chart switcher. */

/** Toggle options displayed in the analytics chart type selector. */
export const ANALYTICS_CHART_TOGGLE_ITEMS = [{
  label: "Line chart",
  value: "line",
  icon: <LineChartIcon />
}, {
  label: "Bar chart",
  value: "bar",
  icon: <BarChartIcon />
}];

/**
 * Space reserved below the plot for the X-axis ticks and labels, and to the left of it for the
 * Y-axis ones.
 *
 * MUI X Charts v8 changed how this is expressed: `margin` used to include the axes, and is now
 * purely outer spacing that each axis's own `height`/`width` is added to. These values split the
 * 70px that the pre-migration `margin` reserved on each of those sides, so the plot keeps the same
 * geometry instead of losing space to the axes being counted twice.
 */
export const CHART_AXIS_RESERVED_SPACE = 70;

/** Part of {@link CHART_AXIS_RESERVED_SPACE} that the X-axis itself takes. */
export const CHART_X_AXIS_HEIGHT = 40;

/** Part of {@link CHART_AXIS_RESERVED_SPACE} that the Y-axis itself takes. */
export const CHART_Y_AXIS_WIDTH = 60;

/** Overall height of the analytics charts. */
export const CHART_HEIGHT = 360;

/** Outer spacing around the plot, excluding the space the axes reserve for themselves. */
export const CHART_MARGIN = {
  top: 32,
  right: 32,
  bottom: CHART_AXIS_RESERVED_SPACE - CHART_X_AXIS_HEIGHT,
  left: CHART_AXIS_RESERVED_SPACE - CHART_Y_AXIS_WIDTH
};

/**
 * Style of the axis labels. `fontSize` and `lineHeight` are both required: from v8 the axis
 * measures its label to size itself, and anything left out of this style is left out of that
 * measurement.
 */
export const CHART_AXIS_LABEL_STYLE = {
  fontSize: 14,
  lineHeight: 1.25
};

/** Display metadata used to render a metric inside the analytics chart. */

/**
 * Chart display metadata per metric, keyed by the analytics API metric key.
 * Acts as the single source of truth for how each metric is labelled in the chart
 * when it is selected from the KPI bar.
 */
export const METRIC_CHART_METADATA = {
  sheetsAdded: {
    chartTitle: "Sheets added all projects",
    yAxisLabel: "Number of sheets added",
    seriesUnitLabel: "sheets",
    tableColumnLabel: "Sheets added"
  },
  sheetsTotal: {
    chartTitle: "Total sheets all projects",
    yAxisLabel: "Total number of sheets",
    seriesUnitLabel: "sheets",
    tableColumnLabel: "Total sheets"
  },
  imagesAdded: {
    chartTitle: "360 images added all projects",
    yAxisLabel: "Number of 360 images added",
    seriesUnitLabel: "images",
    tableColumnLabel: "360 images added"
  },
  imagesTotal: {
    chartTitle: "Total 360 images all projects",
    yAxisLabel: "Total number of 360 images",
    seriesUnitLabel: "images",
    tableColumnLabel: "Total 360 images"
  },
  projectsCount: {
    chartTitle: "Projects in the workspace",
    yAxisLabel: "Number of projects",
    seriesUnitLabel: "projects",
    tableColumnLabel: "Total projects"
  },
  invitedMembers: {
    chartTitle: "Invited members all projects",
    yAxisLabel: "Number of invited members",
    seriesUnitLabel: "members",
    tableColumnLabel: "Invited members"
  },
  newProjects: {
    chartTitle: "New projects",
    yAxisLabel: "Number of new projects",
    seriesUnitLabel: "projects",
    tableColumnLabel: "New projects"
  }
};

/** Metric selected by default when the analytics page is first opened. */
export const DEFAULT_METRIC_KEY = "sheetsAdded";

/** The column definition for the analytics projects breakdown table with the field property typed. */

/** The column fields that can be used in the analytics projects breakdown table. */

/** Required table columns for the analytics projects breakdown table. */
export const ANALYTICS_REQUIRED_TABLE_COLUMNS = ["projectName", "totalViews", "projectManager", "group", "createdDate", "options"];

/** Human-readable labels for the analytics projects breakdown table columns. */
export const ANALYTICS_TABLE_COLUMN_LABELS = {
  projectName: "Project Name",
  totalViews: "Total Views",
  projectManager: "Project Manager",
  group: "Group",
  createdDate: "Created",
  options: "Options"
};

/** The type representing a project item in the analytics projects breakdown. */